import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Trophy, Save, Loader2 } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function LevelsPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [cfg, setCfg] = useState(null);
  const [top, setTop] = useState([]);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/levels-config/${active.id}`).then((r) => setCfg(r.data));
    api.get(`/features/levels/${active.id}?limit=10`).then((r) => setTop(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;
  if (!cfg) return <div className="text-zinc-500 font-mono-tabular text-sm">LOADING…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/features/levels-config/${active.id}`, cfg);
      toast.success("Levels config saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="levels-panel" className="space-y-8 max-w-4xl">
      <div>
        <div className="overline mb-2"><Trophy className="inline w-4 h-4 mr-2 -mt-1" />ESSENTIALS</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Levels</h1>
        <p className="text-zinc-400 mt-2">XP system that rewards active members. The bot tracks every message.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 tactical-card p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">XP per message</label>
              <input type="number" className="tactical-input" value={cfg.xp_per_message} onChange={(e) => set("xp_per_message", parseInt(e.target.value) || 0)} data-testid="lvl-xp" />
            </div>
            <div>
              <label className="overline block mb-2">Cooldown (seconds)</label>
              <input type="number" className="tactical-input" value={cfg.cooldown_sec} onChange={(e) => set("cooldown_sec", parseInt(e.target.value) || 0)} data-testid="lvl-cooldown" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Announce channel (level-up message)</label>
            <select className="tactical-input" value={cfg.announce_channel_id || ""} onChange={(e) => set("announce_channel_id", e.target.value || null)} data-testid="lvl-channel">
              <option value="">— Same channel as message —</option>
              {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <button type="button" onClick={() => set("enabled", !cfg.enabled)} className={`w-full p-3 border flex items-center justify-between rounded-lg ${cfg.enabled ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`} data-testid="lvl-toggle">
            <span className="font-heading uppercase tracking-wider text-sm">Levels Enabled</span>
            <span className={`w-10 h-5 relative rounded-full ${cfg.enabled ? "bg-brand-orange" : "bg-brand-line"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full ${cfg.enabled ? "left-5" : "left-0.5"}`} />
            </span>
          </button>
          <div className="flex justify-end pt-2">
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="lvl-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        <div className="tactical-card p-6">
          <h2 className="font-heading text-lg font-bold uppercase mb-4">Top 10</h2>
          <div className="space-y-2">
            {top.length === 0 && <div className="text-zinc-500 font-mono-tabular text-xs">No XP data yet — chat to earn XP.</div>}
            {top.map((u, i) => (
              <div key={u.user_id} className="flex items-center gap-3 border border-brand-line p-2 rounded-lg" data-testid={`lvl-top-${i}`}>
                <div className="font-mono-tabular text-brand-orange w-6 text-right">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{u.username}</div>
                  <div className="font-mono-tabular text-[10px] text-zinc-500">LVL {u.level || 0} · {u.xp} XP</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
